import type { Shoot } from '../types/portal'

/**
 * DATES AS A CLIENT READS THEM — 8 Oct 2026.
 *
 * `formatShootDate` in utils/time.ts gives "Friday 25 September", which is
 * right for a studio list and wrong as 72px display type: the weekday
 * doubles the length of the line and pushes the month onto a second row.
 *
 * A RUN IS ONE DATE, NOT TWO. The Marjan interviews are 26–30 October.
 * Printing that as two full dates reads as two separate shoots, and the
 * tracker mock caught the other half of this the hard way: an en dash left
 * dangling at a line end because the column was too narrow. One string,
 * the month said once, and nothing to wrap in the middle.
 *
 * Shared by the shoot dates and the project tracker, so a date never reads
 * two different ways on the same page.
 */

function parse(iso: string): Date | null {
  if (!iso) return null
  const date = new Date(`${iso}T00:00:00`)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "13 October" */
export function dayMonth(iso: string): string {
  const date = parse(iso)
  return date ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : ''
}

/**
 * Any start/end pair, as one line of date.
 *
 * A single day is "13 October". A run inside one month says the month once
 * — "26–30 October". A run across two months spells both short, "30 Oct –
 * 2 Nov", because "30 – 2 November" is not a date anybody can read. No start
 * date at all returns '' — the caller draws the dash, because what an
 * unscheduled thing should say depends on what it is.
 */
export function dateRange(startIso: string, endIso?: string): string {
  const start = parse(startIso)
  if (!start) return ''

  const end = endIso ? parse(endIso) : null
  if (!end || end.getTime() <= start.getTime()) return dayMonth(startIso)

  const sameMonth =
    end.getMonth() === start.getMonth() && end.getFullYear() === start.getFullYear()

  if (sameMonth) {
    const month = start.toLocaleDateString('en-GB', { month: 'long' })
    return `${start.getDate()}–${end.getDate()} ${month}`
  }

  const short = { day: 'numeric', month: 'short' } as const
  return `${start.toLocaleDateString('en-GB', short)} – ${end.toLocaleDateString('en-GB', short)}`
}

/** One shoot, as one line of date. */
export function shootRange(shoot: Shoot): string {
  return dateRange(shoot.date, shoot.endDate)
}

/** "Friday", or "Monday to Friday" for a run. The quiet line under the date. */
export function weekdays(startIso: string, endIso?: string): string {
  const start = parse(startIso)
  if (!start) return ''
  const end = endIso ? parse(endIso) : null
  const weekday = (date: Date) => date.toLocaleDateString('en-GB', { weekday: 'long' })
  if (!end || end.getTime() <= start.getTime()) return weekday(start)
  return `${weekday(start)} to ${weekday(end)}`
}

export function shootDays(shoot: Shoot): string {
  return weekdays(shoot.date, shoot.endDate)
}

/**
 * Where today sits against a step: before it, during it, or after it.
 *
 * The tracker's crossbar is computed from this rather than hardcoded — it
 * walks down the page on its own as the week passes, and that is most of why
 * it earns its place. A step with no date is never 'now' and never 'past':
 * an unscheduled thing has not happened and is not happening.
 */
export function stepWhen(startIso: string, endIso?: string, today: Date = new Date()): 'past' | 'now' | 'future' | 'unset' {
  const start = parse(startIso)
  if (!start) return 'unset'
  const midnight = new Date(today)
  midnight.setHours(0, 0, 0, 0)
  const last = endIso ? parse(endIso) ?? start : start
  if (midnight.getTime() < start.getTime()) return 'future'
  if (midnight.getTime() <= last.getTime()) return 'now'
  return 'past'
}
